<template>
	<div v-if="hasMore || loading || total" ref="sentinel" class="list-more">
		<button
			v-if="hasMore"
			type="button"
			class="list-more__button"
			:disabled="loading"
			@click="$emit('load')"
		>
			<i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
			<span>{{ loading ? t('overviewList.loading') : t('overviewList.loadMore') }}</span>
		</button>
		<p v-else-if="total" class="list-more__end" aria-hidden="true">
			{{ t('overviewList.end', {count: total}) }}
		</p>
		<span class="list-more__status" role="status" aria-live="polite">
			{{ loading ? t('overviewList.loading') : '' }}
		</span>
	</div>
</template>

<script setup>
import {onBeforeUnmount, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';

const props = defineProps({
	hasMore: {
		type: Boolean,
		default: false
	},
	loading: {
		type: Boolean,
		default: false
	},
	total: {
		type: Number,
		default: 0
	},
	// How far ahead of the fold to start fetching, so the next page is
	// usually there by the time the user reaches the end of this one.
	rootMargin: {
		type: String,
		default: '400px'
	}
});

const emit = defineEmits(['load']);

const {t} = useI18n();
const sentinel = ref(null);
let observer = null;

function disconnect() {
	if (observer) {
		observer.disconnect();
		observer = null;
	}
}

function observe(element) {
	disconnect();
	// Not every environment has IntersectionObserver (jsdom does not). The
	// button below is the real control; the observer only presses it early,
	// so its absence degrades to a manual "show more" rather than a broken
	// list.
	if (!element || typeof IntersectionObserver === 'undefined') return;
	observer = new IntersectionObserver(
		entries => {
			if (!entries.some(entry => entry.isIntersecting)) return;
			if (props.hasMore && !props.loading) emit('load');
		},
		{rootMargin: props.rootMargin}
	);
	observer.observe(element);
}

watch(sentinel, element => observe(element), {immediate: true});

// A page that arrives without filling the viewport never scrolls, so the
// observer would never fire again; re-check once the new items are in.
watch(
	() => props.loading,
	isLoading => {
		if (!isLoading && sentinel.value) observe(sentinel.value);
	}
);

onBeforeUnmount(disconnect);
</script>

<style lang="scss">
@use "@/scss/sections/overview/list-more";
</style>
